import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Switch,
  FormControlLabel,
  LinearProgress,
} from '@mui/material';
import {
  CalendarToday,
  Payment,
  Warning,
  CheckCircle,
  Cancel,
  Edit,
  Star,
  StarBorder,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { getPartnerSubscription, updatePartnerSubscription } from '../api';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

const PartnerSubscription = () => {
  const { partnerId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [updateStep, setUpdateStep] = useState(0);
  
  const [subscriptionForm, setSubscriptionForm] = useState({
    subscription_tier: '',
    subscription_status: '',
    payment_details: '',
    auto_renewal: true,
    billing_cycle: 'transaction_based',
    custom_features: [],
  });

  const subscriptionTiers = {
    Free: {
      name: 'Free Subscription',
      price: 'Free',
      originalPrice: null,
      discount: null,
      features: [
        'View microsavings of mothers',
        'View credit scores of mothers',
        'Earn your mothers interest on their microsavings',
        'Basic analytics dashboard',
        'Standard customer support',
        'Email notifications',
        'Mobile app access'
      ],
      color: '#BBFFC1', // Company green
      icon: <StarBorder />,
      popular: false,
      description: 'Ideal for organisations with limited funds',
      buttonText: 'Current Plan',
      buttonVariant: 'outlined',
      transactionFee: '0%',
      idealFor: 'Organisations with limited funds'
    },
    'Partner Plus': {
      name: 'Partner Plus',
      price: '3.5% transaction fee',
      originalPrice: null,
      discount: null,
      features: [
        'Everything in Free Subscription',
        'Platform to matchfund towards unlimited mothers of your choice',
        'Advanced analytics dashboard',
        'Priority customer support',
        'Real-time notifications',
        'Advanced reporting & insights',
        'API access & integrations',
        'Custom branding options',
        'Advanced security features'
      ],
      color: '#f92955', // Company pink
      icon: <Star />,
      popular: true,
      description: 'Ideal for organisations or individuals looking for greater climate impact using their matchfunding',
      buttonText: 'Upgrade to Partner Plus',
      buttonVariant: 'contained',
      transactionFee: '3.5%',
      idealFor: 'Organisations or individuals looking for greater climate impact using their matchfunding'
    },
    Premium: {
      name: 'Premium',
      price: '5% transaction fee',
      originalPrice: null,
      discount: null,
      features: [
        'Everything in Partner Plus Subscription',
        'Opportunity to onboard mothers willing to take on your business or organisation\'s activities',
        'Premium analytics dashboard',
        '24/7 dedicated support',
        'Custom reporting & white-label',
        'Advanced API & webhooks',
        'Multi-user management',
        'Advanced security & compliance',
        'Custom integrations',
        'Performance optimization',
        'Dedicated account manager'
      ],
      color: '#f92955', // Company pink
      icon: <Star />,
      popular: false,
      description: 'Ideal for organisations looking for a great impact and sales strategy',
      buttonText: 'Upgrade to Premium',
      buttonVariant: 'outlined',
      transactionFee: '5%',
      idealFor: 'Organisations looking for a great impact and sales strategy'
    },
  };

  const billingCycles = [
    { value: 'transaction_based', label: 'Transaction-based fees', description: 'Fees applied per matched funding transaction' },
    { value: 'monthly', label: 'Monthly reporting', description: 'Monthly reports and analytics' },
    { value: 'quarterly', label: 'Quarterly reviews', description: 'Quarterly performance reviews' },
  ];

  useEffect(() => {
    const fetchSubscription = async () => {
      setLoading(true);
      setError(null);
      try {
        const subscriptionData = await getPartnerSubscription(partnerId);
        setSubscription(subscriptionData.subscription_id ? subscriptionData : null);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, [partnerId]);

  const handleSubscriptionUpdate = async () => {
    try {
      if (!subscription?.subscription_id) {
        throw new Error('No subscription found to update');
      }
      
      const updateData = {};
      if (subscriptionForm.subscription_tier) updateData.subscription_tier = subscriptionForm.subscription_tier;
      if (subscriptionForm.subscription_status) updateData.subscription_status = subscriptionForm.subscription_status;
      if (subscriptionForm.payment_details) updateData.payment_details = subscriptionForm.payment_details;
      if (subscriptionForm.billing_cycle) updateData.billing_cycle = subscriptionForm.billing_cycle;
      if (subscriptionForm.auto_renewal !== undefined) updateData.auto_renewal = subscriptionForm.auto_renewal;

      await updatePartnerSubscription(subscription.subscription_id, updateData);
      const updatedSubscription = await getPartnerSubscription(partnerId);
      setSubscription(updatedSubscription.subscription_id ? updatedSubscription : null);
      setSubscriptionForm({ subscription_tier: '', subscription_status: '', payment_details: '', auto_renewal: true, billing_cycle: 'transaction_based', custom_features: [] });
      setShowUpdateDialog(false);
      setUpdateStep(0);
      alert('Subscription updated successfully');
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubscribe = (tierName) => {
    setSubscriptionForm({
      ...subscriptionForm,
      subscription_tier: tierName,
      subscription_status: 'pending'
    });
    setShowUpdateDialog(true);
    setUpdateStep(0);
  };

  const getCurrentTierInfo = () => {
    if (!subscription) return null;
    return subscriptionTiers[subscription.subscription_tier] || null;
  };



  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'active': return <CheckCircle />;
      case 'inactive': return <Cancel />;
      case 'pending': return <Warning />;
      case 'expired': return <Cancel />;
      default: return <Warning />;
    }
  };

  const calculateUsagePercentage = () => {
    if (!subscription) return 0;
    const currentTier = subscriptionTiers[subscription.subscription_tier];
    if (!currentTier) return 0;
    
    // Mock usage calculation - replace with actual data
    const maxTransactions = subscription.subscription_tier === 'Basic' ? 1000 : 
                           subscription.subscription_tier === 'Gold' ? 5000 : 999999;
    const usedTransactions = 750; // Mock data
    return Math.min((usedTransactions / maxTransactions) * 100, 100);
  };



  const handleUpdateDialogOpen = () => {
    setShowUpdateDialog(true);
    setUpdateStep(0);
  };

  const handleUpdateDialogClose = () => {
    setShowUpdateDialog(false);
    setUpdateStep(0);
  };

  const nextStep = () => {
    setUpdateStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setUpdateStep((prev) => prev - 1);
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        bgcolor: '#FFFFFF'
      }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Loading subscription details...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: isMobile ? 2 : 4, 
      bgcolor: '#FFFFFF', 
      minHeight: '100vh'
    }}>
      {/* Enhanced Header Section */}
      <Box sx={{ 
        mb: 6,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 4,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: { xs: 4, md: 6 },
        color: 'white',
        boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)'
      }}>
        {/* Background Pattern */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255,255,255,0.05) 0%, transparent 50%)
          `,
          zIndex: 1
        }} />
        
        {/* Floating Elements */}
        <Box sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.1)',
          animation: 'float 6s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' }
          }
        }} />
        
        <Box sx={{
          position: 'absolute',
          bottom: 30,
          left: 30,
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.08)',
          animation: 'float 8s ease-in-out infinite reverse',
        }} />

        <Box sx={{ position: 'relative', zIndex: 2 }}>
        <Typography
            variant="h2"
          sx={{
              fontWeight: 900,
              fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem' },
              mb: 2,
              textAlign: { xs: 'center', md: 'left' },
              background: 'linear-gradient(45deg, #ffffff 30%, #f0f0f0 90%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textShadow: '0 4px 8px rgba(0,0,0,0.1)',
              letterSpacing: '-0.02em'
          }}
        >
          Subscription Management
        </Typography>
        
          <Typography
            variant="h5"
            sx={{
              fontWeight: 500,
              opacity: 0.95,
              textAlign: { xs: 'center', md: 'left' },
              maxWidth: 600,
              lineHeight: 1.4,
              fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
            }}
          >
            Unlock your full potential with our premium subscription plans designed for maximum impact
          </Typography>
          
          {/* Stats Row */}
          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 2, md: 4 }, 
            mt: 4,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', md: 'flex-start' }
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                {subscription ? '1' : '0'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.9rem' }}>
                Active Plans
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                3
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.9rem' }}>
                Available Tiers
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                24/7
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.9rem' }}>
                Support
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}
        >
          {error}
        </Alert>
      )}

      {!error && (
        <>
          {/* Enhanced Current Subscription Status */}
          {subscription && (
            <Card sx={{ 
              mb: 6, 
              borderRadius: 4, 
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              overflow: 'hidden',
              position: 'relative',
              border: `3px solid ${getCurrentTierInfo()?.color || '#f92955'}`,
              background: `linear-gradient(135deg, ${getCurrentTierInfo()?.color || '#f92955'} 0%, ${getCurrentTierInfo()?.color || '#f92955'}dd 100%)`,
              transform: 'translateY(0)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: `0 32px 80px ${getCurrentTierInfo()?.color || '#f92955'}40`
              }
            }}>
              {/* Background Pattern */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `
                  radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%)
                `,
                zIndex: 1
              }} />

              <Box sx={{
                p: { xs: 3, md: 4 },
                color: 'white',
                position: 'relative',
                zIndex: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Box sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        border: '2px solid rgba(255,255,255,0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {getCurrentTierInfo()?.icon}
                      </Box>
                      <Typography variant="h3" sx={{ 
                        fontWeight: 900, 
                        mb: 0,
                        fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                        textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}>
                        {subscription.subscription_tier}
                    </Typography>
                    </Box>
                    
                    <Typography variant="h5" sx={{ 
                      opacity: 0.95, 
                      mb: 2,
                      fontWeight: 600,
                      fontSize: { xs: '1.1rem', sm: '1.3rem' }
                    }}>
                      {getCurrentTierInfo()?.price} • {getCurrentTierInfo()?.description}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Chip
                      icon={getStatusIcon(subscription.subscription_status)}
                      label={subscription.subscription_status?.toUpperCase() || 'N/A'}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                          fontWeight: 800,
                          fontSize: '0.9rem',
                          py: 1,
                          px: 2,
                          border: '1px solid rgba(255,255,255,0.3)',
                          '& .MuiChip-icon': { color: 'white' },
                          backdropFilter: 'blur(10px)'
                        }}
                      />
                      
                      <Chip
                        label={`${getCurrentTierInfo()?.transactionFee} Transaction Fee`}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          py: 0.5,
                          px: 1.5,
                          border: '1px solid rgba(255,255,255,0.2)',
                          backdropFilter: 'blur(10px)'
                      }}
                    />
                  </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={handleUpdateDialogOpen}
                      sx={{ 
                        borderColor: 'rgba(255,255,255,0.8)',
                        color: 'white',
                        fontWeight: 700,
                        px: 4,
                        py: 1.5,
                        borderRadius: 3,
                        fontSize: '1rem',
                        textTransform: 'none',
                        backdropFilter: 'blur(10px)',
                        '&:hover': { 
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.15)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      Manage Plan
                    </Button>
                  </Box>
                </Box>
              </Box>

              <CardContent sx={{ 
                p: { xs: 3, md: 4 },
                bgcolor: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(20px)'
              }}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h5" sx={{ 
                      mb: 3, 
                      fontWeight: 800, 
                      color: '#333',
                      fontSize: { xs: '1.2rem', sm: '1.4rem' }
                    }}>
                      📅 Plan Details
                    </Typography>
                    <List dense sx={{ bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2, p: 2 }}>
                      <ListItem sx={{ mb: 1 }}>
                        <ListItemIcon>
                          <CalendarToday sx={{ color: getCurrentTierInfo()?.color || '#f92955' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Start Date"
                          secondary={new Date(subscription.start_date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                          primaryTypographyProps={{ fontWeight: 600, color: '#333' }}
                          secondaryTypographyProps={{ color: '#666', fontWeight: 500 }}
                        />
                      </ListItem>
                      <ListItem sx={{ mb: 1 }}>
                        <ListItemIcon>
                          <CalendarToday sx={{ color: getCurrentTierInfo()?.color || '#f92955' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="End Date"
                          secondary={subscription.end_date ? 
                            new Date(subscription.end_date).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            }) : 'N/A'
                          }
                          primaryTypographyProps={{ fontWeight: 600, color: '#333' }}
                          secondaryTypographyProps={{ color: '#666', fontWeight: 500 }}
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Payment sx={{ color: getCurrentTierInfo()?.color || '#f92955' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Billing Cycle"
                          secondary={subscription.billing_cycle || 'Monthly'}
                          primaryTypographyProps={{ fontWeight: 600, color: '#333' }}
                          secondaryTypographyProps={{ color: '#666', fontWeight: 500 }}
                        />
                      </ListItem>
                    </List>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="h5" sx={{ 
                      mb: 3, 
                      fontWeight: 800, 
                      color: '#333',
                      fontSize: { xs: '1.2rem', sm: '1.4rem' }
                    }}>
                      📊 Usage Analytics
                    </Typography>
                    <Box sx={{ 
                      bgcolor: 'rgba(255,255,255,0.7)', 
                      borderRadius: 2, 
                      p: 3,
                      border: '1px solid rgba(0,0,0,0.1)'
                    }}>
                      <Box sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="body1" sx={{ 
                            color: '#333', 
                            fontWeight: 600 
                          }}>
                            Transaction Usage
                          </Typography>
                          <Typography variant="h6" sx={{ 
                            color: getCurrentTierInfo()?.color || '#f92955', 
                            fontWeight: 900 
                          }}>
                          {Math.round(calculateUsagePercentage())}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={calculateUsagePercentage()} 
                        sx={{ 
                            height: 12, 
                            borderRadius: 6,
                            bgcolor: 'rgba(0,0,0,0.1)',
                          '& .MuiLinearProgress-bar': {
                              borderRadius: 6,
                              background: `linear-gradient(90deg, ${getCurrentTierInfo()?.color || '#f92955'} 0%, ${getCurrentTierInfo()?.color || '#f92955'}dd 100%)`,
                              boxShadow: `0 2px 8px ${getCurrentTierInfo()?.color || '#f92955'}40`
                          }
                        }}
                      />
                    </Box>
                      <Typography variant="body2" sx={{ 
                        color: '#666', 
                        fontWeight: 500,
                        textAlign: 'center',
                        bgcolor: 'rgba(0,0,0,0.05)',
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px solid rgba(0,0,0,0.1)'
                      }}>
                      {subscription.subscription_tier === 'Premium' ? 'Unlimited' : 
                       `${750} / ${subscription.subscription_tier === 'Basic' ? '1000' : '5000'} transactions used`}
                    </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {/* Enhanced Subscription Plans Header */}
          <Card sx={{ 
            borderRadius: 4, 
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            overflow: 'hidden',
            border: '1px solid #f0f0f0',
            mb: 6,
            position: 'relative'
          }}>
            <Box sx={{ 
              p: { xs: 4, md: 6 }, 
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              {/* Enhanced Background Pattern */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: `
                  radial-gradient(circle at 20% 80%, rgba(255,255,255,0.15) 0%, transparent 50%),
                  radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
                  radial-gradient(circle at 40% 40%, rgba(255,255,255,0.08) 0%, transparent 50%),
                  radial-gradient(circle at 60% 60%, rgba(255,255,255,0.05) 0%, transparent 50%)
                `,
                zIndex: 1
              }} />
              
              {/* Floating Elements */}
              <Box sx={{
                position: 'absolute',
                top: 30,
                left: 30,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
                animation: 'float 8s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px) rotate(0deg)' },
                  '50%': { transform: 'translateY(-30px) rotate(180deg)' }
                }
              }} />
              
              <Box sx={{
                position: 'absolute',
                bottom: 40,
                right: 40,
                width: 60,
                height: 60,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.08)',
                animation: 'float 10s ease-in-out infinite reverse',
              }} />
              
              <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.05)',
                animation: 'pulse 4s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.5 },
                  '50%': { transform: 'translate(-50%, -50%) scale(1.2)', opacity: 0.2 }
                }
              }} />
              
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography variant="h1" sx={{ 
                  fontWeight: 900, 
                  mb: 3, 
                  color: '#ffffff',
                  fontSize: { xs: '2.5rem', sm: '3rem', md: '4rem', lg: '5rem' },
                  letterSpacing: '-0.03em',
                  textShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  background: 'linear-gradient(45deg, #ffffff 30%, #f0f0f0 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Choose Your Plan
                </Typography>
                <Typography variant="h4" sx={{ 
                  color: '#ffffff', 
                  mb: 3,
                  fontWeight: 700,
                  fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.8rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  opacity: 0.95
                }}>
                  Select the perfect plan for your impact goals
                </Typography>
                <Typography variant="h6" sx={{ 
                  color: '#ffffff',
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.3rem' },
                  maxWidth: 700,
                  mx: 'auto',
                  lineHeight: 1.6,
                  opacity: 0.9,
                  fontWeight: 400
                }}>
                  All plans include our core features with different impact levels and support tiers. 
                  Start your journey towards greater impact today.
                </Typography>
                
                {/* Feature Highlights */}
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 2, md: 4 }, 
                  mt: 4,
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    <CheckCircle sx={{ fontSize: 20, color: '#BBFFC1' }} />
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                      No Setup Fees
                </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    <CheckCircle sx={{ fontSize: 20, color: '#BBFFC1' }} />
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                      Instant Activation
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    bgcolor: 'rgba(255,255,255,0.15)',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)'
                  }}>
                    <CheckCircle sx={{ fontSize: 20, color: '#BBFFC1' }} />
                    <Typography variant="body2" sx={{ color: 'white', fontWeight: 600 }}>
                      24/7 Support
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 2, md: 4 }, 
                justifyContent: 'center',
                flexDirection: { xs: 'column', lg: 'row' },
                alignItems: { xs: 'center', lg: 'stretch' },
                width: '100%'
              }}>
                {Object.entries(subscriptionTiers).map(([key, tier]) => (
                  <Card
                    key={key}
                    sx={{
                      width: { xs: '100%', lg: 'calc(33.333% - 21px)' },
                      maxWidth: { xs: '450px', lg: 'none' },
                      minWidth: { xs: 'auto', lg: '320px' },
                      height: '100%',
                      position: 'relative',
                      borderRadius: 4,
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: subscription?.subscription_tier === key ? 3 : 1,
                      borderColor: subscription?.subscription_tier === key ? tier.color : 'rgba(0,0,0,0.1)',
                      boxShadow: subscription?.subscription_tier === key ? 
                        `0 20px 60px ${tier.color}40, 0 8px 32px rgba(0,0,0,0.12)` : 
                        '0 8px 32px rgba(0,0,0,0.08)',
                      '&:hover': {
                        transform: { xs: 'none', lg: 'translateY(-16px)' },
                        boxShadow: `0 32px 80px ${tier.color}30, 0 16px 48px rgba(0,0,0,0.15)`,
                        borderColor: tier.color,
                      },
                      overflow: 'visible',
                      minHeight: { xs: 'auto', lg: 620 },
                      display: 'flex',
                      flexDirection: 'column',
                      background: subscription?.subscription_tier === key ? 
                        `linear-gradient(135deg, ${tier.color}08 0%, #ffffff 50%, ${tier.color}05 100%)` :
                        'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                      backdropFilter: 'blur(20px)'
                    }}
                  >
                    {/* Popular Badge */}
                    {tier.popular && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -20,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: tier.color,
                          color: 'white',
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          fontSize: '0.875rem',
                          fontWeight: 900,
                          zIndex: 10,
                          boxShadow: `0 12px 32px ${tier.color}60`,
                          textTransform: 'uppercase',
                          letterSpacing: '0.8px',
                          border: '2px solid white',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '100%',
                            height: '100%',
                            background: tier.color,
                            borderRadius: 3,
                            zIndex: -1,
                            filter: 'blur(8px)',
                            opacity: 0.6
                          }
                        }}
                      >
                        ⭐ MOST POPULAR
                      </Box>
                    )}

                    {/* Current Plan Badge */}
                    {subscription?.subscription_tier === key && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -16,
                          right: 16,
                          bgcolor: '#BBFFC1',
                          color: '#000',
                          px: 3,
                          py: 1,
                          borderRadius: 3,
                          fontSize: '0.8rem',
                          fontWeight: 900,
                          zIndex: 10,
                          boxShadow: '0 8px 24px rgba(187, 255, 193, 0.6)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          border: '2px solid white',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '100%',
                            height: '100%',
                            background: '#BBFFC1',
                            borderRadius: 3,
                            zIndex: -1,
                            filter: 'blur(6px)',
                            opacity: 0.4
                          }
                        }}
                      >
                        ✓ CURRENT PLAN
                      </Box>
                    )}

                    <CardContent sx={{ 
                      p: { xs: 3, md: 4 }, 
                      textAlign: 'center', 
                      display: 'flex', 
                      flexDirection: 'column', 
                      flex: 1,
                      position: 'relative'
                    }}>
                      {/* Background Pattern for each card */}
                      <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `
                          radial-gradient(circle at 20% 80%, ${tier.color}08 0%, transparent 50%),
                          radial-gradient(circle at 80% 20%, ${tier.color}05 0%, transparent 50%)
                        `,
                        zIndex: 1,
                        borderRadius: 4
                      }} />

                      <Box sx={{ position: 'relative', zIndex: 2 }}>
                      {/* Tier Header */}
                        <Box sx={{ mb: 4 }}>
                        <Box sx={{ 
                          color: tier.color, 
                            mb: 3,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <Box sx={{
                              p: 2.5,
                              borderRadius: 3,
                            bgcolor: `${tier.color}15`,
                              border: `3px solid ${tier.color}30`,
                              boxShadow: `0 8px 24px ${tier.color}30`,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'scale(1.1) rotate(5deg)',
                                boxShadow: `0 12px 32px ${tier.color}40`
                              }
                            }}>
                              {React.cloneElement(tier.icon, { 
                                sx: { 
                                  fontSize: 32, 
                                  color: tier.color,
                                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                                } 
                              })}
                          </Box>
                        </Box>
                        <Typography variant="h4" sx={{ 
                            fontWeight: 900, 
                            mb: 2, 
                          color: '#000',
                            fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' },
                            letterSpacing: '-0.02em',
                            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }}>
                          {tier.name}
                        </Typography>
                          
                          {/* Description */}
                          <Typography variant="body2" sx={{ 
                            color: '#666', 
                            mb: 3,
                            lineHeight: 1.5,
                            fontSize: '0.9rem',
                            fontStyle: 'italic'
                          }}>
                            {tier.description}
                          </Typography>
                      </Box>

                        {/* Enhanced Pricing */}
                        <Box sx={{ 
                          mb: 4, 
                          p: 3, 
                          bgcolor: `${tier.color}10`, 
                          borderRadius: 3,
                          border: `2px solid ${tier.color}20`,
                          position: 'relative',
                          overflow: 'hidden'
                        }}>
                          {/* Pricing background pattern */}
                          <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: `
                              radial-gradient(circle at 30% 70%, ${tier.color}15 0%, transparent 50%),
                              radial-gradient(circle at 70% 30%, ${tier.color}10 0%, transparent 50%)
                            `,
                            zIndex: 1
                          }} />
                          
                          <Box sx={{ position: 'relative', zIndex: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                          <Typography variant="h3" sx={{ 
                            fontWeight: 900, 
                            color: tier.color,
                                fontSize: { xs: '1.6rem', sm: '1.8rem', md: '2.2rem' },
                                textShadow: `0 2px 4px ${tier.color}30`
                          }}>
                            {tier.price}
                          </Typography>
                            </Box>
                            
                            {/* Transaction Fee Badge */}
                            <Box sx={{
                              display: 'inline-block',
                              bgcolor: tier.color,
                              color: 'white',
                              px: 2,
                              py: 0.5,
                              borderRadius: 2,
                              fontSize: '0.8rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              boxShadow: `0 4px 12px ${tier.color}40`
                            }}>
                              {tier.transactionFee} Transaction Fee
                            </Box>
                        </Box>
                      </Box>

                        {/* Enhanced Features */}
                        <Box sx={{ mb: 4, flex: 1 }}>
                        <Typography variant="h6" sx={{ 
                            mb: 2, 
                            fontWeight: 800, 
                          color: '#333',
                            fontSize: '1.1rem',
                            textAlign: 'left',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <Box sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: tier.color
                            }} />
                          Core Benefits
                        </Typography>
                        <List dense sx={{ textAlign: 'left' }}>
                          {tier.features.map((feature, index) => (
                              <ListItem key={index} sx={{ px: 0, py: 0.8 }}>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                <Box sx={{
                                    width: 24,
                                    height: 24,
                                  borderRadius: '50%',
                                  bgcolor: `${tier.color}20`,
                                  display: 'flex',
                                  alignItems: 'center',
                                    justifyContent: 'center',
                                    border: `2px solid ${tier.color}30`,
                                    boxShadow: `0 2px 8px ${tier.color}20`
                                }}>
                                    <CheckCircle sx={{ fontSize: 16, color: tier.color }} />
                                </Box>
                              </ListItemIcon>
                              <ListItemText 
                                primary={feature}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  sx: { 
                                    color: '#333',
                                    fontWeight: 500,
                                    lineHeight: 1.4,
                                      fontSize: '0.85rem'
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>

                        {/* Enhanced Subscribe Button */}
                      <Box sx={{ mt: 'auto' }}>
                        <Button
                          variant={subscription?.subscription_tier === key ? "outlined" : tier.buttonVariant}
                          fullWidth
                          size="large"
                          onClick={() => handleSubscribe(key)}
                          disabled={subscription?.subscription_tier === key}
                          sx={{
                              py: 2.5,
                            px: 4,
                            borderRadius: 3,
                            fontWeight: 800,
                            fontSize: '1.1rem',
                            borderColor: tier.color,
                            color: subscription?.subscription_tier === key ? tier.color : 'white',
                            bgcolor: subscription?.subscription_tier === key ? 'transparent' : tier.color,
                            textTransform: 'none',
                              letterSpacing: '0.8px',
                            boxShadow: subscription?.subscription_tier === key ? 'none' : `0 8px 24px ${tier.color}40`,
                              position: 'relative',
                              overflow: 'hidden',
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                                transition: 'left 0.5s',
                                zIndex: 1
                              },
                            '&:hover': {
                              bgcolor: subscription?.subscription_tier === key ? 
                                `${tier.color}15` : tier.color,
                              borderColor: tier.color,
                                transform: 'translateY(-3px)',
                                boxShadow: subscription?.subscription_tier === key ? 'none' : `0 16px 40px ${tier.color}50`,
                                '&::before': {
                                  left: '100%'
                                }
                            },
                            '&:active': {
                                transform: 'translateY(-1px)',
                            },
                            '&:disabled': {
                              bgcolor: 'transparent',
                              color: tier.color,
                              borderColor: tier.color,
                              transform: 'none',
                              boxShadow: 'none'
                            },
                              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                          }}
                        >
                            <Box sx={{ position: 'relative', zIndex: 2 }}>
                          {subscription?.subscription_tier === key ? 'Current Plan' : tier.buttonText}
                            </Box>
                        </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Update Subscription Dialog */}
      <Dialog 
        open={showUpdateDialog} 
        onClose={handleUpdateDialogClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {subscription ? 'Update Subscription' : 'Create Subscription'}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Stepper activeStep={updateStep} sx={{ mb: 4 }}>
            <Step>
              <StepLabel>Plan Selection</StepLabel>
            </Step>
            <Step>
              <StepLabel>Billing Details</StepLabel>
            </Step>
            <Step>
              <StepLabel>Confirmation</StepLabel>
            </Step>
          </Stepper>

          {updateStep === 0 && (
            <Box>
              <Typography variant="h5" sx={{ mb: 4, fontWeight: 700, color: '#333' }}>
                Select Subscription Tier
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 3, 
                justifyContent: 'center',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'center', md: 'stretch' },
                width: '100%'
              }}>
                {Object.entries(subscriptionTiers).map(([key, tier]) => (
                  <Card
                    key={key}
                    sx={{
                      width: { xs: '100%', md: 'calc(33.333% - 16px)' },
                      maxWidth: { xs: '400px', md: 'none' },
                      minWidth: { xs: 'auto', md: '280px' },
                      cursor: 'pointer',
                      border: subscriptionForm.subscription_tier === key ? 3 : 1,
                      borderColor: subscriptionForm.subscription_tier === key ? tier.color : 'divider',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      background: subscriptionForm.subscription_tier === key ? 
                        `linear-gradient(135deg, ${tier.color}08 0%, ${tier.color}15 100%)` : 
                        'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                      '&:hover': {
                        transform: { xs: 'none', md: 'translateY(-4px)' },
                        boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                        borderColor: tier.color,
                      }
                    }}
                    onClick={() => setSubscriptionForm({ ...subscriptionForm, subscription_tier: key })}
                  >
                    {/* Popular Badge */}
                    {tier.popular && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -12,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          bgcolor: tier.color,
                          color: 'white',
                          px: 3,
                          py: 0.5,
                          borderRadius: 2,
                          fontSize: '0.875rem',
                          fontWeight: 800,
                          zIndex: 1,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        
                      </Box>
                    )}

                    {/* Current Plan Badge */}
                    {subscription?.subscription_tier === key && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -12,
                          right: 16,
                          bgcolor: '#BBFFC1',
                          color: '#000',
                          px: 2,
                          py: 0.5,
                          borderRadius: 2,
                          fontSize: '0.75rem',
                          fontWeight: 800,
                          zIndex: 1,
                          boxShadow: '0 4px 16px rgba(187, 255, 193, 0.4)',
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}
                      >
                        ✓ Current Plan
                      </Box>
                    )}

                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Box sx={{ color: tier.color, mb: 2, display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: `${tier.color}15`,
                          border: `2px solid ${tier.color}30`
                        }}>
                          {React.cloneElement(tier.icon, { sx: { fontSize: 20, color: tier.color } })}
                        </Box>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#000' }}>
                        {tier.name}
                      </Typography>
                      <Typography variant="h5" sx={{ color: tier.color, fontWeight: 800, mb: 1 }}>
                        {tier.price}
                      </Typography>
                       <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#333', textAlign: 'left', fontSize: '0.9rem' }}>
                         Core Benefits
                       </Typography>
                      <List dense sx={{ textAlign: 'left' }}>
                        {tier.features.slice(0, 4).map((feature, index) => (
                          <ListItem key={index} sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <Box sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: `${tier.color}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <CheckCircle sx={{ fontSize: 12, color: tier.color }} />
                              </Box>
                            </ListItemIcon>
                            <ListItemText 
                              primary={feature}
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { 
                                  color: '#333',
                                  fontWeight: 500,
                                  lineHeight: 1.3,
                                  fontSize: '0.8rem'
                                }
                              }}
                            />
                          </ListItem>
                        ))}
                        {tier.features.length > 4 && (
                          <ListItem sx={{ px: 0 }}>
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <Box sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                bgcolor: `${tier.color}20`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <Typography variant="caption" sx={{ color: tier.color, fontWeight: 700, fontSize: '0.7rem' }}>
                                  +{tier.features.length - 4}
                                </Typography>
                              </Box>
                            </ListItemIcon>
                            <ListItemText 
                              primary="More benefits included"
                              primaryTypographyProps={{
                                variant: 'body2',
                                sx: { 
                                  color: '#666',
                                  fontStyle: 'italic',
                                  fontSize: '0.8rem'
                                }
                              }}
                            />
                          </ListItem>
                        )}
                      </List>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            </Box>
          )}

          {updateStep === 1 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Billing Configuration
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Billing Cycle</InputLabel>
                    <Select
                      value={subscriptionForm.billing_cycle}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, billing_cycle: e.target.value })}
                      label="Billing Cycle"
                    >
                      {billingCycles.map((cycle) => (
                        <MenuItem key={cycle.value} value={cycle.value}>
                          {cycle.label} {cycle.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Subscription Status</InputLabel>
                    <Select
                      value={subscriptionForm.subscription_status}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscription_status: e.target.value })}
                      label="Subscription Status"
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Payment Details"
                    value={subscriptionForm.payment_details}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, payment_details: e.target.value })}
                    fullWidth
                    placeholder="Enter payment method details"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={subscriptionForm.auto_renewal}
                        onChange={(e) => setSubscriptionForm({ ...subscriptionForm, auto_renewal: e.target.checked })}
                      />
                    }
                    label="Auto-renewal"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {updateStep === 2 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Review & Confirm
              </Typography>
              <Card sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Tier:</strong> {subscriptionForm.subscription_tier}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Status:</strong> {subscriptionForm.subscription_status}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Billing Cycle:</strong> {subscriptionForm.billing_cycle}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <strong>Auto-renewal:</strong> {subscriptionForm.auto_renewal ? 'Yes' : 'No'}
                </Typography>
                {subscriptionForm.payment_details && (
                  <Typography variant="body1">
                    <strong>Payment Details:</strong> {subscriptionForm.payment_details}
                  </Typography>
                )}
              </Card>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={handleUpdateDialogClose} 
            sx={{ color: '#f92955' }}
          >
            Cancel
          </Button>
          {updateStep > 0 && (
            <Button 
              onClick={prevStep}
              sx={{ color: '#f92955' }}
            >
              Back
            </Button>
          )}
          {updateStep < 2 ? (
            <Button 
              variant="contained" 
              onClick={nextStep}
              disabled={updateStep === 0 && !subscriptionForm.subscription_tier}
              sx={{ 
                bgcolor: '#f92955',
                '&:hover': { bgcolor: '#BBFFC1' },
                '&:disabled': { bgcolor: '#ccc' }
              }}
            >
              Next
            </Button>
          ) : (
            <Button 
              variant="contained" 
              onClick={handleSubscriptionUpdate}
              disabled={!subscriptionForm.subscription_tier || !subscriptionForm.subscription_status}
              sx={{ 
                bgcolor: '#f92955',
                '&:hover': { bgcolor: '#BBFFC1' },
                '&:disabled': { bgcolor: '#ccc' }
              }}
            >
              {subscription ? 'Update Subscription' : 'Create Subscription'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PartnerSubscription;